import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';

const API_BASE = 'https://birdseye-main-326804163209.asia-southeast1.run.app';
const POLL_INTERVAL_MS = 10 * 60 * 1000;

// ─── GodJSON parser ────────────────────────────────────────────────────────────
/**
 * Converts GodJSON ammonia.readings[] into chart-ready data.
 *
 * Each reading looks like:
 *   {
 *     iso8601_timestamp_string: "2026-04-26T14-49-00",
 *     wet_litter_percentage: 67.69,
 *     ammonia_value_mean: 0.33,
 *     bird_count: 67,
 *     ...
 *   }
 */
const parseReadings = (data) => {
  const readings = data?.ammonia?.readings ?? [];
  return readings.map((entry) => {
    const isoStr = entry?.iso8601_timestamp_string ?? null;
    let dayLabel = '—';
    let rawDate = null;

    if (isoStr) {
      try {
        // Normalize "2026-04-26T14-49-00" → "2026-04-26T14:49:00"
        const normalized = isoStr.replace(/T(\d{2})-(\d{2})-(\d{2})/, 'T$1:$2:$3');
        rawDate = new Date(normalized);
        dayLabel = rawDate.toLocaleString(undefined, {
          month: 'short', day: 'numeric',
          hour: '2-digit', minute: '2-digit',
        });
      } catch {}
    }

    return {
      day:       dayLabel,
      wetLitter: entry.wet_litter_percentage ?? 0,
      ammonia:   entry.ammonia_value_mean ?? 0,
      birds:     entry.bird_count ?? 0,
      rawDate,
    };
  });
};

/**
 * Filter readings to only those within the selected range (in hours).
 * Falls back to all data if we have no valid dates.
 */
const filterByRange = (allData, hours) => {
  const hasValidDates = allData.some((d) => d.rawDate instanceof Date);
  if (!hasValidDates || hours === null) return allData;

  const cutoff = new Date(Date.now() - hours * 60 * 60 * 1000);
  const filtered = allData.filter((d) => d.rawDate && d.rawDate >= cutoff);
  return filtered.length > 0 ? filtered : allData;
};

const RANGES = [
  { label: 'Past 24 hours', hours: 24 },
  { label: 'Past 3 days',   hours: 72 },
  { label: 'Past 5 days',   hours: 120 },
  { label: 'Past 7 days',   hours: 168 },
];

// ─── Nav Icons ─────────────────────────────────────────────────────────────────
const navIcons = {
  Dashboard: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    </svg>
  ),
  Cameras: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  ),
  History: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="12 8 12 12 14 14"/>
      <path d="M3.05 11a9 9 0 1 0 .5-4.5"/><polyline points="3 3 3 7 7 3"/>
    </svg>
  ),
  Footage: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <path d="M10 11l5 3-5 3v-6z"/>
    </svg>
  ),
  Profile: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Settings: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  Logout: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
  Team: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="7" r="3" />
      <path d="M3 20c1.5-3 4-4.5 6-4.5s4.5 1.5 6 4.5" />
      <path d="M17 11c1 0 2 .8 2 2v1" />
    </svg>
  ),
};

// ─── Custom Tooltip ────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-2xl px-4 py-3 shadow-xl">
      <p className="text-[11px] font-poppins text-gray-400 mb-2 uppercase tracking-widest">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2 mb-1 last:mb-0">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: entry.color }} />
          <p className="text-sm font-poppins font-semibold text-gray-700">
            {entry.name}:&nbsp;
            <span style={{ color: entry.color }}>
              {entry.name === 'Wet Litter %'
                ? `${entry.value.toFixed(1)}%`
                : `${entry.value.toFixed(2)} ppm`}
            </span>
          </p>
        </div>
      ))}
    </div>
  );
};

// ─── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, icon, accent, bgFrom, bgTo, textColor }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    className="relative rounded-3xl p-5 overflow-hidden shadow-sm border border-black/[0.04]"
    style={{ background: `linear-gradient(135deg, ${bgFrom}, ${bgTo})` }}
  >
    <div className="absolute -bottom-4 -right-4 w-20 h-20 rounded-full opacity-20 pointer-events-none"
      style={{ background: accent }} />
    <div className="flex items-start justify-between mb-3">
      <div className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-sm"
        style={{ background: accent }}>
        <span style={{ color: '#fff' }}>{icon}</span>
      </div>
      <span className="text-[10px] font-poppins font-bold uppercase tracking-widest px-2.5 py-1 rounded-full text-white/80"
        style={{ background: 'rgba(0,0,0,0.12)' }}>
        {sub}
      </span>
    </div>
    <p className="text-2xl font-poppins font-black mb-0.5" style={{ color: textColor }}>{value}</p>
    <p className="text-xs font-poppins font-medium" style={{ color: textColor, opacity: 0.65 }}>{label}</p>
  </motion.div>
);

// ─── Trend Badge ───────────────────────────────────────────────────────────────
const TrendBadge = ({ value, trend }) => {
  const up = trend === 'up';
  return (
    <div className={`flex items-center gap-1 text-xs font-poppins font-semibold px-2.5 py-1 rounded-full
      ${up ? 'bg-red-50 text-red-500' : 'bg-emerald-50 text-emerald-500'}`}>
      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
        {up
          ? <><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></>
          : <><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></>}
      </svg>
      {value}
    </div>
  );
};

// ─── Empty / Loading States ────────────────────────────────────────────────────
const EmptyChart = ({ loading }) => (
  <div className="flex flex-col items-center justify-center h-[170px] gap-2">
    {loading ? (
      <>
        <div className="w-8 h-8 border-2 border-[#AC4242]/30 border-t-[#AC4242] rounded-full animate-spin" />
        <p className="text-xs font-poppins text-gray-400">Loading data…</p>
      </>
    ) : (
      <>
        <svg className="w-10 h-10 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <p className="text-xs font-poppins text-gray-400">No readings in this range</p>
      </>
    )}
  </div>
);

// ─── History Page ──────────────────────────────────────────────────────────────
const History = () => {
  const navigate = useNavigate();

  const [allData, setAllData]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [fetchError, setFetchError]     = useState(false);
  const [range, setRange]               = useState(RANGES[0]);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Auth guard
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) navigate('/login');
  }, [navigate]);

  // ── GodJSON fetch ────────────────────────────────────────────────────────────
  const fetchGodJson = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE}/information/getgodjson`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setAllData(parseReadings(json));
      setFetchError(false);
    } catch (err) {
      console.warn('GodJSON fetch failed:', err.message);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGodJson();
    const id = setInterval(fetchGodJson, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchGodJson]);

  // ── Derived data ─────────────────────────────────────────────────────────────
  const data        = filterByRange(allData, range.hours);
  const hasData     = data.length > 0;

  const lastWet     = hasData ? data[data.length - 1].wetLitter : 0;
  const lastAmmonia = hasData ? data[data.length - 1].ammonia   : 0;
  const avgWet      = hasData
    ? (data.reduce((s, d) => s + d.wetLitter, 0) / data.length).toFixed(1)
    : '0.0';
  const avgAmmonia  = hasData
    ? (data.reduce((s, d) => s + d.ammonia, 0) / data.length).toFixed(2)
    : '0.00';

  const wetTrend     = hasData && data.length > 1 && lastWet     > data[0].wetLitter ? 'up' : 'down';
  const ammoniaTrend = hasData && data.length > 1 && lastAmmonia > data[0].ammonia   ? 'up' : 'down';

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
    navigate('/login');
  };

  const navItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Cameras',   path: '/cameras'   },
    { label: 'History',   path: '/history', active: true },
    { label: 'Footage',   path: '/footage'   },
    { label: 'Team',      path: '/team'      },
    { label: 'Profile',   path: '/profile'   },
    { label: 'Settings',  path: '/settings'  },
  ];

  const axisProps = {
    tick: { fontSize: 11, fontFamily: 'Poppins', fill: '#c4b5b5' },
    axisLine: false,
    tickLine: false,
  };
  const gridProps = { strokeDasharray: '4 4', stroke: '#f0e8e8', vertical: false };

  const wetIcon = (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2C6 9 4 13 4 16a8 8 0 0 0 16 0c0-3-2-7-8-14z"/>
    </svg>
  );
  const ammoniaIcon = (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v18h18"/><path d="M7 16l4-8 4 8"/>
    </svg>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <motion.div
      className="h-screen flex overflow-hidden bg-[#F5EFE7]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
    >
      {/* ── Sidebar ── */}
      <div className="w-56 flex-shrink-0 p-3">
        <div className="h-full bg-[#AC4242] rounded-3xl flex flex-col">
          <div className="p-6 flex justify-center">
            <img src="/images/logonav.png" alt="BirdsEye" className="h-32 w-auto" />
          </div>
          <nav className="flex-1 px-4 space-y-1">
            {navItems.map(item => (
              <button
                key={item.label}
                onClick={() => navigate(item.path)}
                className={`flex items-center gap-3 px-4 py-3 w-full text-left text-white rounded-xl font-poppins text-sm transition-all duration-200 hover:scale-[1.02]
                  ${item.active ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'}`}
              >
                {navIcons[item.label]}
                {item.label}
              </button>
            ))}
          </nav>
          <div className="px-4 pb-6">
            <button onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 w-full text-left text-white/60 hover:text-white hover:bg-white/10 rounded-xl font-poppins text-sm transition-all duration-200">
              {navIcons.Logout} Logout
            </button>
          </div>
        </div>
      </div>

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Topbar */}
        <div className="bg-[#F5EFE7] border-b border-black/[0.07] px-8 py-4 flex items-center justify-between flex-shrink-0">
          <img src="/images/loginlogo.png" alt="BirdsEye" className="h-12 w-auto" />
          <span className="text-xs font-poppins text-gray-400">{localStorage.getItem('userEmail') || ''}</span>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-8 py-7">

          {/* ── Header row ── */}
          <div className="flex items-center justify-between mb-7">
            <h1 className="text-5xl font-poppins font-bold text-[#AC4242] tracking-tight leading-none">
              History
            </h1>

            <div className="flex items-center gap-3">
              {!loading && !fetchError && hasData && (
                <div className="flex items-center gap-1.5 text-xs font-poppins text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1.5">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                  Live data · {allData.length} reading{allData.length !== 1 ? 's' : ''}
                </div>
              )}
              {fetchError && (
                <div className="flex items-center gap-1.5 text-xs font-poppins text-red-500 bg-red-50 border border-red-100 rounded-full px-3 py-1.5">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full" />
                  Connection error
                  <button onClick={fetchGodJson} className="underline font-semibold ml-1">Retry</button>
                </div>
              )}

              {/* Range dropdown */}
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(o => !o)}
                  className="flex items-center gap-2.5 bg-white border border-black/[0.08] rounded-2xl px-5 py-2.5 text-sm font-poppins font-semibold text-gray-700 shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <span className="w-2 h-2 rounded-full bg-[#AC4242]" />
                  {range.label}
                  <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 bg-white border border-gray-100 rounded-2xl shadow-2xl overflow-hidden z-20 w-44">
                    {RANGES.map(r => (
                      <button
                        key={r.label}
                        onClick={() => { setRange(r); setDropdownOpen(false); }}
                        className={`w-full text-left px-5 py-3 text-sm font-poppins transition-colors duration-150 flex items-center gap-2
                          ${r.label === range.label ? 'text-[#AC4242] font-semibold bg-[#FDF0F0]' : 'text-gray-500 hover:bg-gray-50'}`}
                      >
                        {r.label === range.label && <span className="w-1.5 h-1.5 rounded-full bg-[#AC4242]" />}
                        {r.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Stat Cards ── */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <StatCard
              label="Latest Wet Litter"
              value={loading ? '—' : `${lastWet.toFixed(1)}%`}
              sub="Latest"
              icon={wetIcon}
              accent="#AC4242"
              bgFrom="#ffffff"
              bgTo="#fdf0f0"
              textColor="#7a2020"
            />
            <StatCard
              label="Latest Ammonia"
              value={loading ? '—' : `${lastAmmonia.toFixed(2)} ppm`}
              sub="Latest"
              icon={ammoniaIcon}
              accent="#C9973A"
              bgFrom="#ffffff"
              bgTo="#fdf7ec"
              textColor="#7a5010"
            />
            <StatCard
              label="Avg Wet Litter"
              value={loading ? '—' : `${avgWet}%`}
              sub="Average"
              icon={wetIcon}
              accent="#4A9E8F"
              bgFrom="#ffffff"
              bgTo="#edfaf7"
              textColor="#1a5f54"
            />
            <StatCard
              label="Avg Ammonia"
              value={loading ? '—' : `${avgAmmonia} ppm`}
              sub="Average"
              icon={ammoniaIcon}
              accent="#4A7BBE"
              bgFrom="#ffffff"
              bgTo="#eef4fd"
              textColor="#1a3f7a"
            />
          </div>

          {/* ── Charts grid ── */}
          <div className="grid grid-cols-2 gap-5 mb-5">

            {/* Wet Litter Area Chart */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="bg-white rounded-3xl p-6 border border-black/[0.05] relative overflow-hidden"
            >
              <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#AC4242]/[0.04] pointer-events-none" />
              <div className="flex items-start justify-between mb-1">
                <h2 className="text-base font-poppins font-bold text-gray-800">Wet Litter Percentage</h2>
                {hasData && <TrendBadge value={`${lastWet.toFixed(1)}%`} trend={wetTrend} />}
              </div>
              <p className="text-xs font-poppins text-gray-400 mb-4">Percentage of wet litter detected per reading</p>
              {(!hasData || loading) ? (
                <EmptyChart loading={loading} />
              ) : (
                <ResponsiveContainer width="100%" height={170}>
                  <AreaChart data={data} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
                    <defs>
                      <linearGradient id="wetGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"   stopColor="#AC4242" stopOpacity={0.18} />
                        <stop offset="100%" stopColor="#AC4242" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid {...gridProps} />
                    <XAxis dataKey="day" {...axisProps} />
                    <YAxis {...axisProps} domain={[0, 'auto']} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="wetLitter" name="Wet Litter %" stroke="#AC4242" strokeWidth={2.5}
                      fill="url(#wetGrad)" dot={false} activeDot={{ r: 5, fill: '#AC4242', strokeWidth: 0 }} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </motion.div>

            {/* Ammonia Area Chart */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-3xl p-6 border border-black/[0.05] relative overflow-hidden"
            >
              <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#C9973A]/[0.05] pointer-events-none" />
              <div className="flex items-start justify-between mb-1">
                <h2 className="text-base font-poppins font-bold text-gray-800">Ammonia Levels</h2>
                {hasData && <TrendBadge value={`${lastAmmonia.toFixed(2)} ppm`} trend={ammoniaTrend} />}
              </div>
              <p className="text-xs font-poppins text-gray-400 mb-4">Ammonia concentration in ppm per reading</p>
              {(!hasData || loading) ? (
                <EmptyChart loading={loading} />
              ) : (
                <ResponsiveContainer width="100%" height={170}>
                  <AreaChart data={data} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
                    <defs>
                      <linearGradient id="ammoniaGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"   stopColor="#C9973A" stopOpacity={0.18} />
                        <stop offset="100%" stopColor="#C9973A" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid {...gridProps} />
                    <XAxis dataKey="day" {...axisProps} />
                    <YAxis {...axisProps} domain={[0, 'auto']} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="ammonia" name="Ammonia" stroke="#C9973A" strokeWidth={2.5}
                      fill="url(#ammoniaGrad)" dot={false} activeDot={{ r: 5, fill: '#C9973A', strokeWidth: 0 }} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </motion.div>
          </div>

          {/* ── Combined full-width chart ── */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white rounded-3xl p-6 border border-black/[0.05] relative overflow-hidden mb-2"
          >
            <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-[#AC4242]/[0.03] pointer-events-none" />
            <div className="flex items-start justify-between mb-1">
              <h2 className="text-base font-poppins font-bold text-gray-800">Wet Litter vs. Ammonia</h2>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-0.5 rounded-full bg-[#AC4242] inline-block" />
                  <span className="text-xs font-poppins text-gray-500">Wet Litter %</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-0.5 rounded-full bg-[#C9973A] inline-block" />
                  <span className="text-xs font-poppins text-gray-500">Ammonia</span>
                </div>
              </div>
            </div>
            <p className="text-xs font-poppins text-gray-400 mb-5">
              Correlation between wet litter and ammonia levels over time
            </p>
            {(!hasData || loading) ? (
              <EmptyChart loading={loading} />
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={data} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
                  <defs>
                    <linearGradient id="wetGrad2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor="#AC4242" stopOpacity={0.15} />
                      <stop offset="100%" stopColor="#AC4242" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="ammoniaGrad2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor="#C9973A" stopOpacity={0.12} />
                      <stop offset="100%" stopColor="#C9973A" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="day" {...axisProps} />
                  <YAxis {...axisProps} domain={[0, 'auto']} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="wetLitter" name="Wet Litter %" stroke="#AC4242" strokeWidth={2.5}
                    fill="url(#wetGrad2)" dot={false} activeDot={{ r: 5, fill: '#AC4242', strokeWidth: 0 }} />
                  <Area type="monotone" dataKey="ammonia" name="Ammonia" stroke="#C9973A" strokeWidth={2.5}
                    fill="url(#ammoniaGrad2)" dot={false} activeDot={{ r: 5, fill: '#C9973A', strokeWidth: 0 }} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </motion.div>

        </div>
      </div>
    </motion.div>
  );
};

export default History;
